import { createAction } from '@reduxjs/toolkit'

import { hasAtMostDecimals, normalizeGuid, parseDecimal } from '../../pricing/model/format'
import { wireRef } from '../api/contracts'
import { priceKey } from './keys'
import type {
  AddLineForm,
  AllocationDraft,
  CreateForm,
  FillMode,
  HeaderDraft,
  HeaderField,
  LineDraft,
  PostingPreview,
  PriceType,
  Supply,
  SupplyCommandKind,
  SupplyLine,
} from './types'
import type { StructureWarehouse } from '../../structure/model/types'

/**
 * Команды поставок — plain actions (F05): экран просит, listener исполняет.
 * Тело команды собирают чистые функции ниже: их проверяют тесты, а listener
 * отправляет ровно то, что собрано, и ровно то же — при проверке исхода.
 */
export interface SupplyCommandIntent {
  resourceId: string
  supplyId: string
  kind: SupplyCommandKind
  lineId?: string | null
  /** Уточнение ключа: одна команда на строку/режим, а не на всю поставку. */
  detail?: string
  data: unknown
  query?: Record<string, string> | null
  expectedConfigurationVersion: string
  /** Только для проведения: `lastPostingAttempt` поставки ДО нажатия. */
  baseline?: number
  /**
   * Только для создания: файл накладной, из которого заполнить строки новой поставки сразу после её создания.
   * Контракт службы — две команды (создать, затем `/import`); вторая уходит сама, только когда первая подтверждена.
   */
  invoiceFileId?: string
}

export const supplyCommandRequested = createAction<SupplyCommandIntent>('onec/supplies/commandRequested')
/** «Проверить исход»: та же команда тем же ключом (служба отдаст прежний итог). Не для проведения. */
export const supplyCommandResendRequested = createAction<{ key: string }>('onec/supplies/commandResendRequested')
/** «Проверить статус» проведения: только ЧТЕНИЕ журнала саги, POST не повторяется. */
export const postingStatusCheckRequested = createAction<{ resourceId: string; supplyId: string }>(
  'onec/supplies/postingStatusCheckRequested',
)

export type Built<T> = { ok: true; data: T } | { ok: false; message: string }

const ok = <T>(data: T): Built<T> => ({ ok: true, data })
const refuse = (message: string): Built<never> => ({ ok: false, message })

/** Число из поля: запятая или точка, не больше `places` знаков. Сервер отвергает избыточную точность, а не округляет. */
export function readNumber(input: string, places: number, label: string): Built<number> {
  const value = parseDecimal(input)
  if (value === null) return refuse(`${label}: введите число`)
  if (!hasAtMostDecimals(input, places)) return refuse(`${label}: не больше ${places} знаков после запятой`)
  return ok(value)
}

const today = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).getTime())

const trimmedOrNull = (value: string) => (value.trim() === '' ? null : value.trim())

/** Склад выбирается ВНУТРИ базы окна: из её справочника, не из общего списка. */
function warehouseOf(warehouses: readonly StructureWarehouse[], key: string): StructureWarehouse | undefined {
  return warehouses.find((item) => item.refKey === key.toLowerCase())
}

export function buildCreate(resourceId: string, form: CreateForm, warehouses: readonly StructureWarehouse[]) {
  if (!today(form.documentDate)) return refuse('Дата документа — ГГГГ-ММ-ДД')
  const data: Record<string, unknown> = { documentDate: form.documentDate }
  if (form.warehouseKey !== '') {
    const warehouse = warehouseOf(warehouses, form.warehouseKey)
    if (warehouse === undefined) return refuse('Склад не найден в справочнике этой базы — обновите список складов')
    data.warehouseRef = wireRef(resourceId, warehouse.refKey)
    data.warehouseName = warehouse.name
  }
  if (form.withoutSupplier) {
    data.withoutSupplier = true
  } else if (form.supplierKey.trim() !== '') {
    const key = normalizeGuid(form.supplierKey)
    if (key === null) return refuse('Ключ поставщика в 1С — GUID')
    data.supplierRef = wireRef(resourceId, key)
    data.supplierName = trimmedOrNull(form.supplierName)
  }
  const invoice = trimmedOrNull(form.supplierInvoiceNumber)
  if (invoice !== null) data.supplierInvoiceNumber = invoice
  const comment = trimmedOrNull(form.comment)
  if (comment !== null) data.comment = comment
  return ok(data)
}

/** Поля шапки, которые изменили на сервере после начала правки (конфликт — по полю, не по версии поставки). */
export function headerConflicts(draft: HeaderDraft, server: Record<HeaderField, string>): HeaderField[] {
  return (Object.keys(draft.base) as HeaderField[]).filter((field) => draft.base[field] !== server[field] && draft.values[field] !== server[field])
}

/**
 * Правка шапки: только изменённые поля. Версия — текущая поставки: её двигают и
 * свои автосохранения строк; чужая правка ЭТИХ полей ловится `headerConflicts`
 * до отправки, а гонка в момент записи — 409 сервера.
 */
export function buildHeaderUpdate(resourceId: string, draft: HeaderDraft, supply: Supply, warehouses: readonly StructureWarehouse[]) {
  const data: Record<string, unknown> = { version: supply.version }
  const { values } = draft
  if (values.documentDate !== undefined && values.documentDate !== supply.documentDate) {
    if (!today(values.documentDate)) return refuse('Дата документа — ГГГГ-ММ-ДД')
    data.documentDate = values.documentDate
  }
  if (values.supplierInvoiceNumber !== undefined && values.supplierInvoiceNumber !== (supply.supplierInvoiceNumber ?? '')) {
    data.supplierInvoiceNumber = values.supplierInvoiceNumber.trim()
  }
  if (values.comment !== undefined && values.comment !== (supply.comment ?? '')) data.comment = values.comment
  if (values.warehouseKey !== undefined && values.warehouseKey !== (supply.warehouseRef?.remoteKey ?? '')) {
    const warehouse = warehouseOf(warehouses, values.warehouseKey)
    if (warehouse === undefined) return refuse('Склад не найден в справочнике этой базы — обновите список складов')
    data.warehouseRef = wireRef(resourceId, warehouse.refKey)
    data.warehouseName = warehouse.name
  }
  if (Object.keys(data).length === 1) return refuse('Изменений нет')
  const server: Record<HeaderField, string> = {
    documentDate: supply.documentDate,
    supplierInvoiceNumber: supply.supplierInvoiceNumber ?? '',
    comment: supply.comment ?? '',
    warehouseKey: supply.warehouseRef?.remoteKey ?? '',
  }
  return headerConflicts(draft, server).length > 0 ? refuse('Поля изменили на сервере после начала правки: решите, что оставить') : ok(data)
}

export function buildAddLine(form: AddLineForm) {
  const name = trimmedOrNull(form.name)
  if (name === null) return refuse('Наименование обязательно')
  const quantity = readNumber(form.quantity, 3, 'Количество')
  if (!quantity.ok) return quantity
  if (quantity.data <= 0) return refuse('Количество больше нуля')
  const price = readNumber(form.purchasePrice, 2, 'Цена')
  if (!price.ok) return price
  if (price.data < 0) return refuse('Цена не меньше нуля')
  return ok({
    name,
    quantity: quantity.data,
    purchasePrice: price.data,
    ...(trimmedOrNull(form.article) !== null ? { article: form.article.trim() } : {}),
    ...(trimmedOrNull(form.barcode) !== null ? { barcode: form.barcode.trim() } : {}),
  })
}

/** Правка строки из таблицы. Числа проверяются до отправки; `notes` уходит как набрано. */
export function buildLineUpdate(draft: LineDraft) {
  const data: Record<string, unknown> = { version: draft.baseVersion }
  const fields = [
    ['quantity', 3, 'Количество'],
    ['purchasePrice', 2, 'Цена'],
    ['amount', 2, 'Сумма'],
  ] as const
  for (const [field, places, label] of fields) {
    const raw = draft.values[field]
    if (raw === undefined) continue
    const value = readNumber(raw, places, label)
    if (!value.ok) return value
    if (value.data < 0) return refuse(`${label}: не меньше нуля`)
    data[field] = value.data
  }
  if (draft.values.notes !== undefined) data.notes = draft.values.notes
  if (draft.allocationPolicy !== null) data.allocationPolicy = draft.allocationPolicy
  return ok(data)
}

/** Доли строки: серверные доли + набранные ячейки; нулевые не отправляются. */
export function buildAllocations(resourceId: string, draft: AllocationDraft) {
  const merged: Record<string, number> = { ...draft.base }
  for (const [warehouse, raw] of Object.entries(draft.cells)) {
    if (raw.trim() === '') {
      merged[warehouse] = 0
      continue
    }
    const value = readNumber(raw, 3, 'Количество на складе')
    if (!value.ok) return value
    if (value.data < 0) return refuse('Количество на складе не меньше нуля')
    merged[warehouse] = value.data
  }
  return ok({
    version: draft.baseVersion,
    allocations: Object.entries(merged)
      .filter(([, quantity]) => quantity > 0)
      .map(([warehouse, quantity]) => ({ warehouseRef: wireRef(resourceId, warehouse), quantity })),
  })
}

/** Новые цены по видам: только набранные, вид цены — из справочника ЭТОЙ поставки. */
export function buildPrices(
  resourceId: string,
  typed: Readonly<Record<string, string>>,
  lines: readonly SupplyLine[],
  priceTypes: readonly PriceType[],
) {
  const prices: { supplyLineId: string; priceTypeRef: ReturnType<typeof wireRef>; newPrice: number | null }[] = []
  for (const line of lines) {
    for (const type of priceTypes) {
      const raw = typed[priceKey(line.id, type.ref.remoteKey)]
      if (raw === undefined) continue
      if (raw.trim() === '') {
        prices.push({ supplyLineId: line.id, priceTypeRef: wireRef(resourceId, type.ref.remoteKey), newPrice: null })
        continue
      }
      const value = readNumber(raw, 2, `Цена «${type.name}», строка ${line.lineNumber}`)
      if (!value.ok) return value
      if (value.data <= 0) return refuse(`Цена «${type.name}», строка ${line.lineNumber}: больше нуля`)
      prices.push({ supplyLineId: line.id, priceTypeRef: wireRef(resourceId, type.ref.remoteKey), newPrice: value.data })
    }
  }
  return prices.length === 0 ? refuse('Новых цен не набрано') : ok({ prices })
}

export function buildPriceFactor(input: string, preview: boolean) {
  const factor = readNumber(input, 4, 'Коэффициент')
  if (!factor.ok) return factor
  if (factor.data <= 0) return refuse('Коэффициент больше нуля')
  return ok({ factor: factor.data, preview })
}

export function buildInvoiceFill(fileId: string, mode: FillMode) {
  const id = normalizeGuid(fileId)
  if (id === null) return refuse('Идентификатор файла накладной — GUID из окна «Импорт накладной»')
  return ok({ fileId: id, mode })
}

/**
 * Намерение «Создать поставку»; из окна «Поставка из накладной» — с файлом. Своя команда на файл (`detail`):
 * создание из одной накладной не смешивается ни с обычным созданием, ни с созданием из другой.
 */
export function supplyCreateIntent(resourceId: string, data: unknown, expectedConfigurationVersion: string, invoiceFileId = ''): SupplyCommandIntent {
  const fileId = normalizeGuid(invoiceFileId)
  return {
    resourceId,
    supplyId: '',
    kind: 'create',
    data,
    expectedConfigurationVersion,
    ...(fileId !== null ? { detail: fileId, invoiceFileId: fileId } : {}),
  }
}

export function buildOverrideMatch(productId: string) {
  const id = normalizeGuid(productId)
  if (id === null) return refuse('Ключ товара 1С — GUID')
  return ok({ productId: id })
}

/** Склад для заполнения распределения — из справочника базы окна. */
export function buildAllToOne(resourceId: string, warehouseKey: string) {
  return warehouseKey === '' ? refuse('Выберите склад') : ok({ warehouseRef: wireRef(resourceId, warehouseKey) })
}

export function buildDefaultWarehouse(resourceId: string, warehouseKey: string, supply: Supply) {
  return warehouseKey === '' ? refuse('Выберите склад') : ok({ version: supply.version, warehouseRef: wireRef(resourceId, warehouseKey) })
}

/** Проведение: то, что человек видел в предпросмотре (`previewHash`), и версия поставки. */
export function buildPost(preview: PostingPreview, supply: Supply) {
  if (!preview.canPost) return refuse('Предпросмотр запрещает проведение: устраните проблемы выше')
  return ok({ previewHash: preview.previewHash, version: String(supply.version) })
}
